import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
} from "@mui/material";
import { Add, CheckCircle, Cancel, Sync } from "@mui/icons-material";
import { settingsAPI } from "../../services/api";

const DevOpsSettings = ({ configurationId }) => {
  const [connections, setConnections] = useState([]);
  const [connectionType, setConnectionType] = useState("DevOps");
  const [organizationUrl, setOrganizationUrl] = useState("");
  const [projectName, setProjectName] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [syncing, setSyncing] = useState({});

  useEffect(() => {
    // Clear previous state when configuration changes
    setError("");
    setSuccess("");

    if (configurationId) {
      loadConnections();
    } else {
      setConnections([]);
    }
  }, [configurationId]);

  const loadConnections = async () => {
    if (!configurationId) {
      setConnections([]);
      return;
    }
    try {
      setError("");
      const response = await settingsAPI.getDevOpsConnections(configurationId);
      setConnections(response.data || []);
    } catch (err) {
      console.error("Failed to load connections:", err);
      setError(err.response?.data?.error || "Failed to load connections.");
      setConnections([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!configurationId) {
      setError("Please select a configuration first");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      await settingsAPI.createDevOpsConnection({
        connection_type: connectionType,
        organization_url: organizationUrl,
        project_name: projectName,
        api_token: apiToken,
        configuration_id: configurationId,
      });
      setSuccess("Connection verified and saved successfully!");
      setOrganizationUrl("");
      setProjectName("");
      setApiToken("");
      loadConnections();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to connect");
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (connectionId) => {
    setSyncing({ ...syncing, [connectionId]: true });
    try {
      await settingsAPI.syncTasks(connectionId);
      setSuccess("Tasks synced successfully!");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to sync tasks");
    } finally {
      setSyncing({ ...syncing, [connectionId]: false });
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        DevOps & Jira Connections
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Connect to Azure DevOps or Jira to extract historical tasks and
        estimates.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
        <FormControl fullWidth margin="normal">
          <InputLabel>Connection Type</InputLabel>
          <Select
            value={connectionType}
            onChange={(e) => setConnectionType(e.target.value)}
            label="Connection Type"
          >
            <MenuItem value="DevOps">Azure DevOps</MenuItem>
            <MenuItem value="Jira">Jira</MenuItem>
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label="Organization URL"
          variant="outlined"
          margin="normal"
          value={organizationUrl}
          onChange={(e) => setOrganizationUrl(e.target.value)}
          required
          placeholder="https://dev.azure.com/organization"
        />
        <TextField
          fullWidth
          label="Project Name"
          variant="outlined"
          margin="normal"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          required
        />
        <TextField
          fullWidth
          label="API Token"
          type="password"
          variant="outlined"
          margin="normal"
          value={apiToken}
          onChange={(e) => setApiToken(e.target.value)}
          required
          helperText="The token will be verified before saving"
        />
        <Button
          type="submit"
          variant="contained"
          startIcon={<Add />}
          sx={{ mt: 2 }}
          disabled={loading}
        >
          {loading ? "Verifying..." : "Connect"}
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {connections.map((conn) => (
              <TableRow key={conn.id}>
                <TableCell>{conn.connection_type}</TableCell>
                <TableCell>{conn.organization_url}</TableCell>
                <TableCell>{conn.project_name}</TableCell>
                <TableCell>
                  {conn.is_verified ? (
                    <Chip
                      icon={<CheckCircle />}
                      label="Verified"
                      color="success"
                      size="small"
                    />
                  ) : (
                    <Chip
                      icon={<Cancel />}
                      label="Not Verified"
                      color="error"
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleSync(conn.id)}
                    disabled={syncing[conn.id]}
                  >
                    <Sync />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default DevOpsSettings;
